import React, { FC, ReactElement, useCallback, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { Dropzone, IconImage, ModalV2 } from "@/components";
import { useUploadFile } from "@/hooks";
import { pdf_file_img } from "@/constants";
import {
  ChipStyle,
  ProgressMain,
  UploadActions,
  UploadContainerMain,
  UploadDetails,
  UploadProgressContainer,
  UploadProgressText,
  UploadSize,
} from "./styles.tsx";
import { formatFileSize } from "@/utils/helpers.ts";

interface DocModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  subTitle: string;
  buttonAction: (data: DocumentsDto["document"]) => Promise<void>;
  buttonText: string;
  isLoading: boolean;
}

const UploadDocument: FC<DocModalProps> = ({
  setShow,
  show,
  title,
  subTitle,
  buttonText,
  buttonAction,
  isLoading: isConfirmationLoading,
}): ReactElement => {
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<
    DocumentsDto["document"] | null
  >(null);
  const { isLoading, uploadFile, uploadProgress } = useUploadFile();

  const uploadDocument = useCallback(
    async (file: File) => {
      setDroppedFile(file);

      const data = await uploadFile(file);
      const { key } = data ?? {};

      if (!key) return;

      setUploadedFile({ filename: file.name, fileUrl: key });
    },
    [uploadFile]
  );
  const resetFiles = () => {
    setUploadedFile(null);
    setDroppedFile(null);
  };

  const confirmAddDocument = () => {
    if (uploadedFile === null) return;

    buttonAction({ ...uploadedFile }).finally(() => {
      setShow(false);
      resetFiles();
    });
  };

  return (
    <ModalV2
      setShow={setShow}
      show={show}
      title={title}
      subTitle={subTitle}
      buttonText={buttonText}
      buttonAction={confirmAddDocument}
      isConfirmButtonLoading={isConfirmationLoading}
    >
      <Box>
        <Typography
          sx={{
            marginTop: "10px",
            color: "#030712",
          }}
          variant="body2"
        >
          Upload CBP 7501
        </Typography>
        <Dropzone
          maxSize={2 * 1024 * 1024} // 2 MB
          onUpload={uploadDocument}
          disabled={isLoading || droppedFile !== null}
        />
        {droppedFile !== null && (
          <UploadContainerMain container alignItems="center">
            <Grid item xs={7}>
              <UploadDetails>
                <Box>
                  <IconImage src={pdf_file_img} size="40px" />
                </Box>
                <Box>
                  <Typography variant="body1">{droppedFile.name}</Typography>
                  <UploadSize variant="body2">
                    {formatFileSize(droppedFile.size)}
                  </UploadSize>
                </Box>
              </UploadDetails>
            </Grid>

            <UploadActions item xs={5}>
              <Box>
                <ChipStyle variant="body2">Entry Package</ChipStyle>
              </Box>
              <IconButton
                style={{ padding: "0", margin: "0px 5px 5px 5px" }}
                size="small"
                onClick={resetFiles}
              >
                <Close sx={{ color: "#8E9494" }} />
              </IconButton>
            </UploadActions>

            <ProgressMain>
              <Grid item xs={10.8}>
                <UploadProgressContainer>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    style={{ width: "100%" }}
                  />
                  <UploadProgressText variant="body2">
                    {`${uploadProgress}%`}
                  </UploadProgressText>
                </UploadProgressContainer>
              </Grid>
            </ProgressMain>
          </UploadContainerMain>
        )}
      </Box>
    </ModalV2>
  );
};

export default UploadDocument;
